import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Minus, Trash2, Search, Boxes, RefreshCw, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import {
  inventoryListMaterials, inventoryGetMeta,
  inventorySaveMaterial, inventoryDeleteMaterial, inventoryAdjustStock, inventorySeedCatalog,
  inventoryListSuppliers,
  MATERIAL_CATEGORY_COLORS,
} from "../../../lib/inventory";

const fmtUSD = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Safely turn an API error (string OR FastAPI 422 array of {msg,...}) into a user-readable string.
const parseApiError = (e, fallback = "Save failed") => {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail?.msg || JSON.stringify(detail);
  return String(detail);
};

/**
 * Single-cell inline editor.
 * type: "text" | "number" | "select"
 * placeholder shown when empty.
 */
const EditableCell = ({ value, type = "text", options = [], onCommit, format, placeholder = "—", numeric = false, align = "left", className = "" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  const start = () => {
    setDraft(value ?? "");
    setEditing(true);
  };

  const commit = async () => {
    let next = draft;
    if (type === "number") {
      next = draft === "" || draft === null ? 0 : (numeric ? parseInt(draft, 10) : parseFloat(draft));
      if (Number.isNaN(next)) next = 0;
    } else if (type === "text") {
      next = (draft || "").trim();
    }
    const original = value ?? (type === "number" ? 0 : "");
    if (String(next) === String(original)) { setEditing(false); return; }
    setBusy(true);
    try {
      await onCommit(next);
      setEditing(false);
    } catch {
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => { setEditing(false); setDraft(value ?? ""); };

  if (editing) {
    if (type === "select") {
      return (
        <select
          autoFocus
          value={draft || ""}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Escape") cancel(); if (e.key === "Enter") e.currentTarget.blur(); }}
          className={`input-swiss w-full px-2 py-1 rounded-sm text-[12px] ${className}`}
          disabled={busy}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input
        autoFocus
        type={type === "number" ? "number" : "text"}
        step={type === "number" && !numeric ? "0.01" : undefined}
        min={type === "number" ? 0 : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Escape") cancel(); if (e.key === "Enter") e.currentTarget.blur(); }}
        className={`input-swiss w-full px-2 py-1 rounded-sm text-[12px] ${className}`}
        style={{ textAlign: align }}
        disabled={busy}
      />
    );
  }

  const isEmpty = value == null || value === "" || (type === "number" && Number(value) === 0);
  const display = isEmpty
    ? <em className="text-stone-400">{placeholder}</em>
    : (format ? format(value) : value);

  return (
    <button
      type="button"
      onClick={start}
      className={`w-full text-${align} px-1.5 py-1 rounded-sm hover:bg-[#FEF3C7] focus:bg-[#FEF3C7] outline-none transition truncate ${className}`}
      title="Click to edit"
      style={{ textAlign: align, border: "1px dashed transparent", minHeight: 26 }}
      onMouseEnter={(e) => { e.currentTarget.style.border = "1px dashed var(--ups-border)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.border = "1px dashed transparent"; }}
    >
      {display}
    </button>
  );
};

const CategoryPill = ({ category }) => {
  const c = MATERIAL_CATEGORY_COLORS[category] || MATERIAL_CATEGORY_COLORS.Other;
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] rounded-sm whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
      {category}
    </span>
  );
};

const MaterialForm = ({ initial, meta, suppliers, onSave, onCancel, busy }) => {
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "Cut Sheet Paper",
    size: initial?.size || "",
    unit_label: initial?.unit_label || "1000 sheets",
    price_per_unit: initial?.price_per_unit ?? 0,
    supplier_id: initial?.supplier_id || "",
    stock_qty: initial?.stock_qty ?? 0,
    low_stock_threshold: initial?.low_stock_threshold ?? 0,
    active: initial?.active !== false,
    notes: initial?.notes || "",
  });
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const payload = { ...form, supplier_id: form.supplier_id || null };
        if (!initial?.id) payload.stock_qty = parseInt(form.stock_qty || 0, 10);
        onSave(payload);
      }}
      className="p-4 rounded-sm grid gap-3 sm:grid-cols-3"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="material-form"
    >
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Material name</span>
        <input required value={form.name} onChange={(e) => update("name", e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="material-name" placeholder="e.g. 100# Gloss Cover" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Size (optional)</span>
        <input value={form.size} onChange={(e) => update("size", e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="8.5x11, 54 in, 24x36" />
      </label>

      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Category</span>
        <select required value={form.category} onChange={(e) => update("category", e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="material-category">
          {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Unit (per X)</span>
        <input list="unit-options" value={form.unit_label} onChange={(e) => update("unit_label", e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="1000 sheets / roll / box of 25" />
        <datalist id="unit-options">{meta.common_units.map((u) => <option key={u} value={u} />)}</datalist>
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Negotiated price ({form.unit_label || "unit"})</span>
        <input type="number" min="0" step="0.01" value={form.price_per_unit}
          onChange={(e) => update("price_per_unit", parseFloat(e.target.value) || 0)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono" placeholder="0.00" />
      </label>

      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Approved supplier</span>
        <select value={form.supplier_id} onChange={(e) => update("supplier_id", e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="material-supplier">
          <option value="">— none —</option>
          {suppliers.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Low-stock alert ≤</span>
        <input type="number" min="0" step="1" value={form.low_stock_threshold}
          onChange={(e) => update("low_stock_threshold", parseInt(e.target.value || 0, 10))}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono" />
      </label>

      {!initial?.id && (
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Starting stock</span>
          <input type="number" min="0" step="1" value={form.stock_qty}
            onChange={(e) => update("stock_qty", parseInt(e.target.value || 0, 10))}
            className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono" />
        </label>
      )}
      <label className="block sm:col-span-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Notes</span>
        <textarea rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" />
      </label>

      <label className="sm:col-span-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.active} onChange={(e) => update("active", e.target.checked)} />
        <span>Active (uncheck to hide)</span>
      </label>
      <div className="sm:col-span-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-2 text-[12px] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
        <button type="submit" disabled={busy} className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]" data-testid="material-save">
          {busy ? <Loader2 size={12} className="animate-spin" /> : (initial?.id ? "Update Material" : "Add Material")}
        </button>
      </div>
    </form>
  );
};

const StockCell = ({ qty, threshold, onAdjust, busy }) => {
  const isLow = threshold > 0 && qty <= threshold;
  return (
    <div className="flex items-center gap-1.5 justify-end">
      <button type="button" onClick={() => onAdjust(-1)} disabled={busy || qty <= 0}
        className="w-6 h-6 rounded-sm font-bold flex items-center justify-center disabled:opacity-30"
        style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #DC2626" }}
        title="Decrement (used 1)" aria-label="Decrement">
        <Minus size={12} />
      </button>
      <div
        className="font-mono font-bold text-[14px] min-w-[36px] text-center px-2 py-0.5 rounded-sm"
        style={{
          background: isLow ? "#FEF3C7" : "transparent",
          color: isLow ? "#92400E" : "var(--ups-ink)",
          border: isLow ? "1px solid #F59E0B" : "1px solid transparent",
        }}
        data-testid="stock-qty"
      >
        {qty}
        {isLow && <AlertTriangle size={10} className="inline ml-1" />}
      </div>
      <button type="button" onClick={() => onAdjust(1)} disabled={busy}
        className="w-6 h-6 rounded-sm font-bold flex items-center justify-center"
        style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #16A34A" }}
        title="Increment (received 1)" aria-label="Increment">
        <Plus size={12} />
      </button>
    </div>
  );
};

const MaterialsTab = () => {
  const [materials, setMaterials] = useState([]);
  const [meta, setMeta] = useState({ categories: [], common_units: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ q: "", category: "", supplier: "", lowOnly: false });
  const [groupBy, setGroupBy] = useState("category"); // "category" | "size"
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adjustingId, setAdjustingId] = useState(null);

  // Collapsed groups — persisted in localStorage so admins keep their layout
  // between visits. Key is suffixed by groupBy so collapses don't leak across
  // group modes.
  const collapseStorageKey = `materials_collapsed_${groupBy}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(`materials_collapsed_category`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(collapseStorageKey);
      setCollapsed(new Set(raw ? JSON.parse(raw) : []));
    } catch { setCollapsed(new Set()); }
  }, [collapseStorageKey]);
  const persistCollapsed = (next) => {
    setCollapsed(next);
    try { localStorage.setItem(collapseStorageKey, JSON.stringify(Array.from(next))); } catch { /* ignore quota */ }
  };
  const toggleGroup = (key) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key); else next.add(key);
    persistCollapsed(next);
  };
  const collapseAll = (keys) => persistCollapsed(new Set(keys));
  const expandAll = () => persistCollapsed(new Set());

  const reload = async () => {
    setLoading(true);
    try {
      const [mats, m, sups] = await Promise.all([
        inventoryListMaterials(),
        meta.categories.length === 0 ? inventoryGetMeta() : Promise.resolve(meta),
        inventoryListSuppliers(),
      ]);
      setMaterials(mats);
      if (meta.categories.length === 0) setMeta(m);
      setSuppliers(sups);
    } catch (e) {
      toast.error(parseApiError(e, "Load failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const supMap = useMemo(() => {
    const m = {};
    suppliers.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [suppliers]);

  const filtered = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return materials.filter((m) => {
      if (filter.category && m.category !== filter.category) return false;
      if (filter.supplier === "__none__" && m.supplier_id) return false;
      if (filter.supplier && filter.supplier !== "__none__" && m.supplier_id !== filter.supplier) return false;
      if (filter.lowOnly && !(m.low_stock_threshold > 0 && m.stock_qty <= m.low_stock_threshold)) return false;
      if (q) {
        const hay = `${m.name} ${m.size || ""} ${m.unit_label || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [materials, filter]);

  const grouped = useMemo(() => {
    const g = {};
    if (groupBy === "size") {
      filtered.forEach((m) => {
        const key = m.size || "(no size)";
        g[key] = g[key] || [];
        g[key].push(m);
      });
      // Natural sort: numeric width first, then alpha
      const sortKey = (s) => {
        if (s === "(no size)") return [9999, s];
        const m = String(s).match(/^([0-9.]+)/);
        return [m ? parseFloat(m[1]) : 9999, s];
      };
      const ordered = Object.keys(g).sort((a, b) => {
        const [an, as] = sortKey(a); const [bn, bs] = sortKey(b);
        return an !== bn ? an - bn : as.localeCompare(bs);
      });
      return ordered.map((k) => ({ category: k, items: g[k] }));
    }
    filtered.forEach((m) => {
      g[m.category] = g[m.category] || [];
      g[m.category].push(m);
    });
    const ordered = (meta.categories || []).filter((c) => g[c]);
    return ordered.map((c) => ({ category: c, items: g[c] }));
  }, [filtered, meta, groupBy]);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      await inventorySaveMaterial(editing?.id, payload);
      toast.success(editing?.id ? "Updated" : "Material added");
      setEditing(null);
      await reload();
    } catch (e) { toast.error(parseApiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  // Patch a single field on a material — used by EditableCell. Updates state optimistically.
  const handlePatchField = async (m, field, nextValue) => {
    try {
      const updated = await inventorySaveMaterial(m.id, { [field]: nextValue });
      setMaterials((prev) => prev.map((x) => x.id === m.id ? { ...x, ...updated } : x));
    } catch (e) {
      toast.error(parseApiError(e, "Save failed"));
      throw e;
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete ${m.name}? This cannot be undone.`)) return;
    try { await inventoryDeleteMaterial(m.id); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(parseApiError(e, "Delete failed")); }
  };

  const handleAdjust = async (m, delta) => {
    setAdjustingId(m.id);
    try {
      await inventoryAdjustStock(m.id, delta);
      // Optimistic optimistic update
      setMaterials((prev) => prev.map((x) => x.id === m.id ? { ...x, stock_qty: Math.max(0, x.stock_qty + delta) } : x));
    } catch (e) { toast.error(parseApiError(e, "Adjust failed")); }
    finally { setAdjustingId(null); }
  };

  const handleSeed = async () => {
    if (!window.confirm("Seed default materials catalog? Existing materials are kept untouched.")) return;
    try {
      const r = await inventorySeedCatalog();
      toast.success(`Seeded ${r.inserted} new materials (total ${r.total_materials})`);
      await reload();
    } catch (e) { toast.error("Seed failed"); }
  };

  const lowStockCount = materials.filter((m) => m.low_stock_threshold > 0 && m.stock_qty <= m.low_stock_threshold).length;
  const outOfStockCount = materials.filter((m) => m.stock_qty === 0).length;

  if (loading) return <div className="p-10 text-[color:var(--ups-subtext)] flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Loading materials…</div>;

  return (
    <div className="space-y-4" data-testid="materials-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Boxes size={20} /> Materials</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            Catalog of paper, media, substrates, and supplies you stock. Tap <span className="font-mono">+</span> when shipments come in, <span className="font-mono">−</span> when stock is consumed.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleSeed} className="px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }} data-testid="seed-materials-btn">
            <RefreshCw size={12} /> Seed Catalog
          </button>
          {!editing && (
            <button onClick={() => setEditing({})} className="btn-brutal px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" data-testid="add-material-btn">
              <Plus size={12} /> New Material
            </button>
          )}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card-swiss p-3 rounded-sm">
          <div className="overline">Tracked Items</div>
          <div className="font-display text-2xl font-black mt-0.5">{materials.length}</div>
        </div>
        <div className="card-swiss p-3 rounded-sm" style={{ borderColor: lowStockCount > 0 ? "#F59E0B" : undefined }}>
          <div className="overline" style={{ color: "#92400E" }}>Low Stock</div>
          <div className="font-display text-2xl font-black mt-0.5" style={{ color: lowStockCount > 0 ? "#92400E" : undefined }}>{lowStockCount}</div>
        </div>
        <div className="card-swiss p-3 rounded-sm" style={{ borderColor: outOfStockCount > 0 ? "#DC2626" : undefined }}>
          <div className="overline" style={{ color: "#991B1B" }}>Out of Stock</div>
          <div className="font-display text-2xl font-black mt-0.5" style={{ color: outOfStockCount > 0 ? "#991B1B" : undefined }}>{outOfStockCount}</div>
        </div>
      </div>

      {editing && (
        <MaterialForm
          initial={editing}
          meta={meta}
          suppliers={suppliers}
          busy={busy}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Filters */}
      <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] font-mono text-[color:var(--ups-subtext)]" data-testid="inline-edit-hint">
        <span>💡 Click any field below to edit inline. Changes save automatically.</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => collapseAll(grouped.map((g) => g.category))}
            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center gap-1"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="materials-collapse-all"
          >
            <ChevronRight size={11} /> Collapse all
          </button>
          <button
            type="button"
            onClick={expandAll}
            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center gap-1"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="materials-expand-all"
          >
            <ChevronDown size={11} /> Expand all
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
          <input type="search" placeholder="Search by name, size, unit…" value={filter.q} onChange={(e) => setFilter((p) => ({ ...p, q: e.target.value }))}
            className="input-swiss w-full pl-7 pr-3 py-1.5 rounded-sm text-[12px]" data-testid="material-search" />
        </div>
        <select value={filter.category} onChange={(e) => setFilter((p) => ({ ...p, category: e.target.value }))}
          className="input-swiss px-2 py-1.5 rounded-sm text-[12px]" data-testid="material-filter-category">
          <option value="">All categories</option>
          {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.supplier} onChange={(e) => setFilter((p) => ({ ...p, supplier: e.target.value }))}
          className="input-swiss px-2 py-1.5 rounded-sm text-[12px]">
          <option value="">All suppliers</option>
          <option value="__none__">— No supplier —</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label className="flex items-center gap-1 text-[12px] font-bold uppercase tracking-[0.12em] cursor-pointer">
          <input type="checkbox" checked={filter.lowOnly} onChange={(e) => setFilter((p) => ({ ...p, lowOnly: e.target.checked }))} data-testid="filter-low-only" /> Low only
        </label>
        <div className="ml-auto flex items-center rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-ink)" }} data-testid="material-group-by">
          <span className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] bg-[color:var(--ups-ink)] text-[#FFB500]">Group</span>
          {[
            { id: "category", label: "Category" },
            { id: "size", label: "Size" },
          ].map((opt) => {
            const active = groupBy === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setGroupBy(opt.id)}
                className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em]"
                style={{ background: active ? "var(--ups-gold)" : "#fff", color: "var(--ups-ink)" }}
                data-testid={`group-by-${opt.id}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grouped tables */}
      {grouped.length === 0 ? (
        <div className="p-10 text-center text-[color:var(--ups-subtext)] rounded-sm" style={{ border: "1px dashed var(--ups-border)", background: "#fff" }}>
          No materials match these filters.
        </div>
      ) : (
        grouped.map(({ category, items }) => {
          const isSizeGroup = groupBy === "size";
          // For size groups, header uses a neutral palette; for category groups, use the category color.
          const c = isSizeGroup
            ? { bg: "var(--ups-ink)", fg: "#FFB500", border: "var(--ups-ink)" }
            : (MATERIAL_CATEGORY_COLORS[category] || MATERIAL_CATEGORY_COLORS.Other);
          const isCollapsed = collapsed.has(category);
          return (
            <div key={category} className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }} data-testid={`materials-group-${category}`}>
              <button
                type="button"
                onClick={() => toggleGroup(category)}
                className="w-full px-4 py-2.5 flex items-center justify-between text-left cursor-pointer transition-opacity hover:opacity-90"
                style={{ background: c.bg, color: c.fg, borderBottom: isCollapsed ? "none" : `2px solid ${c.border}` }}
                aria-expanded={!isCollapsed}
                data-testid={`materials-group-toggle-${category}`}
              >
                <div className="flex items-center gap-2">
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  <span className="font-bold text-sm uppercase tracking-[0.14em]">
                    {isSizeGroup ? `Size · ${category}` : category}
                  </span>
                </div>
                <div className="text-[11px] font-mono">{items.length} item{items.length === 1 ? "" : "s"}</div>
              </button>
              {!isCollapsed && (
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left" style={{ background: "var(--ups-muted)" }}>
                    {[
                      "Name",
                      ...(isSizeGroup ? ["Category"] : ["Size"]),
                      "Unit", "Price", "Supplier", "Stock", "Low ≤", "",
                    ].map((h, i) => (
                      <th key={`${h}-${i}`} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em]"
                        style={{ textAlign: ["Stock", "Price", "Low ≤"].includes(h) ? "right" : "left" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.id} className="border-t" style={{ borderColor: "var(--ups-border)" }} data-testid={`material-row-${m.id}`}>
                      <td className="px-2 py-1 font-medium" data-testid={`mat-name-${m.id}`}>
                        <EditableCell
                          value={m.name}
                          type="text"
                          onCommit={(v) => handlePatchField(m, "name", v)}
                        />
                      </td>
                      {isSizeGroup ? (
                        <td className="px-2 py-1">
                          <EditableCell
                            value={m.category}
                            type="select"
                            options={(meta.categories || []).map((c) => ({ value: c, label: c }))}
                            onCommit={(v) => handlePatchField(m, "category", v)}
                            placeholder="— pick category —"
                          />
                        </td>
                      ) : (
                        <td className="px-2 py-1 font-mono text-[11px]">
                          <EditableCell
                            value={m.size}
                            type="text"
                            onCommit={(v) => handlePatchField(m, "size", v)}
                          />
                        </td>
                      )}
                      <td className="px-2 py-1 font-mono text-[11px] text-[color:var(--ups-subtext)]">
                        <EditableCell
                          value={m.unit_label}
                          type="text"
                          onCommit={(v) => handlePatchField(m, "unit_label", v)}
                        />
                      </td>
                      <td className="px-2 py-1 font-mono">
                        <EditableCell
                          value={m.price_per_unit}
                          type="number"
                          align="right"
                          format={(v) => fmtUSD(v)}
                          onCommit={(v) => handlePatchField(m, "price_per_unit", v)}
                          placeholder="$0.00"
                        />
                      </td>
                      <td className="px-2 py-1 text-[11px] max-w-[180px]">
                        <EditableCell
                          value={m.supplier_id}
                          type="select"
                          options={suppliers.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name }))}
                          format={(v) => supMap[v] || <em className="text-[color:var(--ups-subtext)]">deleted</em>}
                          onCommit={(v) => handlePatchField(m, "supplier_id", v || null)}
                          placeholder="— none —"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <StockCell
                          qty={m.stock_qty}
                          threshold={m.low_stock_threshold}
                          busy={adjustingId === m.id}
                          onAdjust={(delta) => handleAdjust(m, delta)}
                        />
                      </td>
                      <td className="px-2 py-1 font-mono text-[11px]">
                        <EditableCell
                          value={m.low_stock_threshold}
                          type="number"
                          numeric
                          align="right"
                          onCommit={(v) => handlePatchField(m, "low_stock_threshold", v)}
                          placeholder="—"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => handleDelete(m)} className="p-1.5 rounded-sm hover:bg-[#FEE2E2]" aria-label="Delete" data-testid={`delete-material-${m.id}`}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default MaterialsTab;
